/**
 * Motion Detection Service
 *
 * Provides continuous motion detection for auto-capture during dice registration.
 * Detects when the scene is stable (dice has settled) and triggers a callback.
 *
 * TICKET-071: Auto-capture for easier dice registration
 */

import { detectMotion } from './camera';
import { createScopedReporter } from './errorReporter';

const SERVICE_TAG = '[MOTION]';
const reporter = createScopedReporter('MOTION');

/** Floor for the motion threshold. Calibrated value never goes below this. */
const STABLE_MOTION_THRESHOLD_FLOOR = 0.015;

/** Default stability duration required before triggering settled callback (ms) */
const DEFAULT_STABILITY_DURATION_MS = 1500;

/** Interval between motion checks (ms) */
const MOTION_CHECK_INTERVAL_MS = 200;

/**
 * How long to sample baseline noise before allowing motion transitions (TICKET-290).
 * On hand-held devices, the natural tremor noise floor is right at the static
 * threshold. Calibrating against the user's actual noise floor lets the same
 * detector work both on a tripod (low floor) and hand-held (higher floor).
 */
const CALIBRATION_DURATION_MS = 600;

/** Multiplier applied to calibrated p90 noise to derive the motion threshold. */
const THRESHOLD_NOISE_MULTIPLIER = 1.5;

/**
 * Number of consecutive above-threshold frames required to count as motion
 * (hysteresis). Single-frame spikes from breath / micro-twitch are ignored,
 * which prevents the stable timer from getting reset by noise.
 */
const MOTION_HYSTERESIS_FRAMES = 3;

/**
 * Motion detection state
 */
export type MotionState = 'detecting' | 'moving' | 'stabilizing' | 'stable';

/**
 * Callback for motion state changes
 */
export type MotionStateCallback = (state: MotionState, stabilityProgress: number) => void;

/**
 * Callback when scene has been stable for required duration
 */
export type SettledCallback = () => void;

/**
 * Options for motion detection
 */
export interface MotionDetectionOptions {
  /** Duration scene must be stable before triggering settled (ms) */
  stabilityDurationMs?: number;
  /** Callback for motion state changes */
  onStateChange?: MotionStateCallback;
  /** Callback when scene has settled */
  onSettled?: SettledCallback;
}

/**
 * Controller for continuous motion detection
 */
export class MotionDetectionController {
  private videoElement: HTMLVideoElement | null = null;
  private isRunning = false;
  private intervalId: ReturnType<typeof setInterval> | null = null;
  private lastFrameData: string | null = null;
  private stableStartTime: number | null = null;
  private currentState: MotionState = 'detecting';
  private settledTriggered = false;

  // Calibration state — adapts the threshold to the actual noise floor (TICKET-290).
  private startedAt = 0;
  private calibrationSamples: number[] = [];
  private calibratedThreshold: number = STABLE_MOTION_THRESHOLD_FLOOR;

  // Hysteresis counter — number of consecutive above-threshold frames so far.
  // Single-frame spikes from breathing / micro-twitch should not reset the
  // stable timer.
  private consecutiveAboveCount = 0;

  private options: Required<MotionDetectionOptions> = {
    stabilityDurationMs: DEFAULT_STABILITY_DURATION_MS,
    onStateChange: () => {},
    onSettled: () => {}
  };

  /**
   * Start continuous motion detection
   *
   * @param videoElement - Video element to monitor
   * @param options - Detection options
   */
  start(videoElement: HTMLVideoElement, options: MotionDetectionOptions = {}): void {
    if (this.isRunning) {
      console.warn(`${SERVICE_TAG} Motion detection already running, restarting`);
      this.stop();
    }

    this.videoElement = videoElement;
    this.options = {
      stabilityDurationMs: options.stabilityDurationMs ?? DEFAULT_STABILITY_DURATION_MS,
      onStateChange: options.onStateChange ?? (() => {}),
      onSettled: options.onSettled ?? (() => {})
    };

    this.isRunning = true;
    this.lastFrameData = null;
    this.stableStartTime = null;
    this.currentState = 'detecting';
    this.settledTriggered = false;
    this.startedAt = Date.now();
    this.calibrationSamples = [];
    this.calibratedThreshold = STABLE_MOTION_THRESHOLD_FLOOR;
    this.consecutiveAboveCount = 0;

    console.info(`${SERVICE_TAG} Starting motion detection`, {
      stabilityDurationMs: this.options.stabilityDurationMs,
      calibrationDurationMs: CALIBRATION_DURATION_MS
    });

    // Start detection interval
    this.intervalId = setInterval(() => {
      this.checkMotion();
    }, MOTION_CHECK_INTERVAL_MS);

    // Initial check
    this.checkMotion();
  }

  /**
   * Stop motion detection
   */
  stop(): void {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }

    this.isRunning = false;
    this.videoElement = null;
    this.lastFrameData = null;
    this.stableStartTime = null;
    this.settledTriggered = false;

    console.info(`${SERVICE_TAG} Motion detection stopped`);
  }

  /**
   * Reset settled state (call after capture to allow re-triggering)
   */
  resetSettled(): void {
    this.settledTriggered = false;
    this.stableStartTime = null;
    this.updateState('detecting');
    console.info(`${SERVICE_TAG} Settled state reset`);
  }

  /**
   * Check if currently running
   */
  isActive(): boolean {
    return this.isRunning;
  }

  /**
   * Get current motion state
   */
  getState(): MotionState {
    return this.currentState;
  }

  /**
   * Check motion against previous frame
   */
  private async checkMotion(): Promise<void> {
    if (!this.videoElement || !this.isRunning) {
      return;
    }

    try {
      // Capture current frame
      const canvas = document.createElement('canvas');
      canvas.width = 128; // Small size for performance
      canvas.height = 128;
      const ctx = canvas.getContext('2d');

      if (!ctx) {
        return;
      }

      ctx.drawImage(this.videoElement, 0, 0, 128, 128);
      const currentFrame = canvas.toDataURL('image/jpeg', 0.5);

      // First frame - no comparison possible
      if (!this.lastFrameData) {
        this.lastFrameData = currentFrame;
        this.updateState('detecting');
        return;
      }

      // Detect motion between frames
      const motionResult = await detectMotion(this.lastFrameData, currentFrame);
      this.lastFrameData = currentFrame;

      // Calibration phase: collect baseline samples and don't transition states yet.
      // The user might begin rolling immediately after `start()` — we balance that
      // by keeping calibration short (CALIBRATION_DURATION_MS).
      const elapsed = Date.now() - this.startedAt;
      if (elapsed < CALIBRATION_DURATION_MS) {
        this.calibrationSamples.push(motionResult.magnitude);
        return;
      }
      // First post-calibration tick — derive the threshold from collected samples.
      if (this.calibrationSamples.length > 0) {
        this.calibratedThreshold = computeCalibratedThreshold(this.calibrationSamples);
        console.info(`${SERVICE_TAG} Calibration complete`, {
          samples: this.calibrationSamples.length,
          threshold: this.calibratedThreshold.toFixed(4),
          floor: STABLE_MOTION_THRESHOLD_FLOOR
        });
        this.calibrationSamples = [];
      }

      const isStable = motionResult.magnitude < this.calibratedThreshold;

      if (isStable) {
        // Reset hysteresis counter — clean stable frame.
        this.consecutiveAboveCount = 0;

        // Scene is stable
        if (this.stableStartTime === null) {
          this.stableStartTime = Date.now();
          this.updateState('stabilizing');
        }

        const stableDuration = Date.now() - this.stableStartTime;
        const progress = Math.min(stableDuration / this.options.stabilityDurationMs, 1);

        if (stableDuration >= this.options.stabilityDurationMs) {
          this.updateState('stable');

          // Trigger settled callback (only once per stable period)
          if (!this.settledTriggered) {
            this.settledTriggered = true;
            console.info(`${SERVICE_TAG} Scene settled, triggering callback`, {
              stableDuration
            });
            this.options.onSettled();
          }
        } else {
          this.options.onStateChange('stabilizing', progress);
        }
      } else {
        // Above-threshold reading. Hysteresis: only treat as real motion once
        // we've seen MOTION_HYSTERESIS_FRAMES in a row. Single-frame spikes
        // from breath / hand tremor are silently absorbed so the stable timer
        // keeps accumulating.
        this.consecutiveAboveCount += 1;
        if (this.consecutiveAboveCount >= MOTION_HYSTERESIS_FRAMES) {
          this.stableStartTime = null;
          this.settledTriggered = false;
          this.updateState('moving');
        }
      }
    } catch (error) {
      reporter.error('Motion check failed', {
        originalError: error instanceof Error ? error : undefined,
        showToast: false
      });
    }
  }

  /**
   * Update and notify state change
   */
  private updateState(newState: MotionState): void {
    if (this.currentState !== newState) {
      this.currentState = newState;
      const progress = newState === 'stable' ? 1 : 0;
      this.options.onStateChange(newState, progress);
    }
  }
}

/**
 * Create a singleton motion detection controller
 */
export function createMotionDetector(): MotionDetectionController {
  return new MotionDetectionController();
}

/**
 * Pick a threshold from a set of baseline noise samples. Uses the 90th
 * percentile times {@link THRESHOLD_NOISE_MULTIPLIER}, clamped to never drop
 * below {@link STABLE_MOTION_THRESHOLD_FLOOR}. Hand-held devices land around
 * 0.020–0.025; tripod stays at the floor.
 *
 * @param samples - Magnitudes collected during calibration. Must be non-empty.
 */
export function computeCalibratedThreshold(samples: number[]): number {
  if (samples.length === 0) return STABLE_MOTION_THRESHOLD_FLOOR;
  const sorted = [...samples].sort((a, b) => a - b);
  // floor((n-1) × 0.9): for n=10 this is index 8 (not 9), so a single-sample
  // spike doesn't dominate. For small calibration windows (~3 samples at
  // 200ms ticks over 600ms) this still picks a reasonable upper-bound sample.
  const p90Index = Math.max(0, Math.floor((sorted.length - 1) * 0.9));
  const p90 = sorted[p90Index];
  return Math.max(STABLE_MOTION_THRESHOLD_FLOOR, p90 * THRESHOLD_NOISE_MULTIPLIER);
}
